/**
 * The routing vocabulary — what an action IS, before anything sends one.
 *
 * PRD §3.10 hangs two distinct systems off one trigger, and RULE-031 is the
 * whole reason this file is a table rather than a pair of booleans:
 *
 * > "notification (Mailgun to internal recipients) and dispatch (GoHighLevel
 * > for autoresponders and campaigns) are two destinations serving two purposes
 * > from one trigger. **Never conflate them into a single 'send lead' setting**"
 *
 * Three consumers read this and they read different columns. The Connect tab
 * builds its chooser from `group`, its row subtitle from `system` and its
 * disabled entries from `offer` (Story 9.1). Story 9.2's dispatcher resolves a
 * handler per `kind`. Story 9.4's scheduler asks `cadenceLabel` what a chip
 * says. A second copy of any column is the one that drifts — the argument
 * `services/funnels/bands.ts` already made for band ladders.
 *
 * IT LIVES IN `packages/schema` BECAUSE BOTH SIDES NEED IT. ARCH §2.3 —
 * `schema ← api ← app` — and this package imports no Cloudflare binding, so the
 * same catalogue serves the editor, the dispatcher and a plain unit test.
 *
 * ON THE NAME `voice`. PRD §3.14 calls the action `start_voice_call` and
 * RULE-030 says it "sits alongside `notify` and `dispatch`… an ordinary action
 * kind with no special-casing anywhere in the dispatcher." `ACTION_KINDS` names
 * kinds by DESTINATION rather than by verb — email, slack, webhook, ghl, voice —
 * so `voice` IS `start_voice_call`, and it is an ordinary member of the enum
 * with no branch of its own. That is RULE-030's substance: the rule is about the
 * absence of bespoke logic, not about the spelling, and `test/routing.test.ts`
 * asserts the absence rather than the word.
 *
 * Story 9.1.
 */
import { ACTION_KINDS, CADENCES, type ActionKind, type Cadence, type RoutingAction } from './funnel'

/**
 * PRD §3.10's opening list, verbatim: "**Notification** — telling a human…
 * **Dispatch** — sending the record somewhere… **Voice offer** — presenting the
 * call button." Three, not two — the voice offer is neither of the other two,
 * and folding it into notification would be RULE-031's mistake in the other
 * direction.
 */
export type ActionSystem = 'notification' | 'dispatch' | 'voice_offer'

/** The chooser's two columns, `editor-connect.html`'s own words. */
export type ActionGroup = 'tell' | 'send'

/**
 * What M1 does with a kind.
 *
 * DECISION-016: "features dated to later milestones do not render at M1", whose
 * stated reason is that a placeholder "reads as broken rather than as
 * not-yet-built". Its one sanctioned exception is the Voice tab, which PRD §7.5
 * asks for by name — and Story 9.1's acceptance criteria extend that exception
 * to the voice ACTION and to nothing else. So Slack, Webhook and GoHighLevel are
 * absent rather than greyed: there is no control to press.
 */
export type ActionOffer =
  /** Offered, and it works. */
  | 'live'
  /** Rendered, disabled, with the milestone named. Voice only. */
  | 'coming-soon'
  /** Not rendered at all. */
  | 'hidden'

export type ActionEntry = {
  /** The chooser's title, and the leading word of an action row's own sentence. */
  readonly label: string
  /** The chooser's one-line description. Verbatim from the Visual Reference. */
  readonly blurb: string
  readonly group: ActionGroup
  readonly system: ActionSystem
  readonly offer: ActionOffer
  /** When it ships. `M1` for anything `live`. */
  readonly milestone: 'M1' | 'M4' | 'M5'
  /**
   * Whether this kind is an outbound INTEGRATION — a service reached with a
   * stored credential or an endpoint, which `integrations.html` draws a card
   * for and which can be tested with a read call.
   *
   * A COLUMN RATHER THAN A PREDICATE somewhere else, because RULE-030 is
   * explicit about where this answer lives: "Resolve behaviour from a table
   * keyed by kind — ACTION_CATALOGUE — rather than from a branch that names
   * one." Story 11.3 first wrote the integrations list as
   * `ACTION_KINDS.filter(k => k !== 'voice')` and
   * `tests/routing-vocabulary.test.ts` refused it, correctly: a filter that
   * names a kind is the branch the rule forbids, wearing a different hat.
   *
   * Voice is the one `false`. It is an ordinary action kind in every other
   * respect (PRD §3.14 — "an action type, not a separate feature"), and it is
   * not an integration because there is no credential to store and nothing to
   * test: it is a call placed from a Durable Object at M5.
   */
  readonly integration: boolean
}

/**
 * Every action kind, and everything any surface needs to know about one.
 *
 * The labels and blurbs are quoted from `design/screens/editor-connect.html`'s
 * "Add an action" card rather than written here — the chooser is a Visual
 * Reference and its copy is part of it.
 */
export const ACTION_CATALOGUE = {
  email: {
    label: 'Email',
    blurb: 'A person or a shared inbox',
    group: 'tell',
    system: 'notification',
    offer: 'live',
    milestone: 'M1',
    integration: true,
  },
  slack: {
    label: 'Slack',
    blurb: 'A channel or a direct message',
    group: 'tell',
    system: 'notification',
    offer: 'hidden',
    milestone: 'M4',
    integration: true,
  },
  voice: {
    label: 'Voice',
    blurb: "Call the lead while they're still on the page",
    group: 'tell',
    system: 'voice_offer',
    offer: 'coming-soon',
    milestone: 'M5',
    integration: false,
  },
  webhook: {
    label: 'Webhook',
    blurb: 'POST the lead and its score to a URL',
    group: 'send',
    system: 'dispatch',
    offer: 'hidden',
    milestone: 'M4',
    integration: true,
  },
  ghl: {
    label: 'GoHighLevel',
    blurb: 'Create the contact and apply a tag',
    group: 'send',
    system: 'dispatch',
    offer: 'hidden',
    milestone: 'M4',
    integration: true,
  },
} as const satisfies Record<ActionKind, ActionEntry>

/** The chooser's columns, in the order the Visual Reference draws them. */
export const ACTION_GROUPS = [
  { id: 'tell', label: 'Tell someone' },
  { id: 'send', label: 'Send it somewhere' },
] as const satisfies readonly { id: ActionGroup; label: string }[]

/** What an action row's subtitle leads with — "Notification · …". */
export const SYSTEM_LABEL: Record<ActionSystem, string> = {
  notification: 'Notification',
  dispatch: 'Dispatch',
  voice_offer: 'Voice offer',
}

/** The kinds in one chooser column, in catalogue order. */
export const kindsInGroup = (group: ActionGroup): ActionKind[] =>
  ACTION_KINDS.filter((kind) => ACTION_CATALOGUE[kind].group === group)

/** The kinds an operator may actually add today. Exactly `['email']` at M1. */
export const OFFERED_KINDS: readonly ActionKind[] = ACTION_KINDS.filter(
  (kind) => ACTION_CATALOGUE[kind].offer === 'live',
)

/* ── cadence ─────────────────────────────────────────────────────────────── */

/**
 * `cadence_day` is 0–6 with **0 = Sunday**, matching `Date.prototype.getDay()`.
 *
 * Written down because Story 9.4's scheduler and this label are the only two
 * readers of the number, and a disagreement between them would send Monday's
 * digest on Sunday without either side looking wrong.
 */
export const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const

/** The time a daily or weekly action defaults to — PRD §3.10's own example. */
export const DEFAULT_CADENCE_AT = '08:00'
/** Monday, from PRD §3.10's "weekly on a chosen day" and the mockup's chip. */
export const DEFAULT_CADENCE_DAY = 1

type CadenceFields = {
  cadence: Cadence
  cadence_at?: string | null
  cadence_day?: number | null
}

/**
 * The chip on an action row: "Instantly" · "Hourly" · "Daily at 08:00" ·
 * "Mondays at 08:00".
 *
 * RULE-033 is why this takes an ACTION and nothing else: "cadence is a property
 * of **each notification action**, not of the recipient and not of the funnel."
 * A signature that accepted a recipient or a funnel would make the wrong shape
 * expressible, and the shape is the rule.
 *
 * Tolerant of a missing time or day rather than throwing. This renders a chip;
 * a half-configured action should read as "Daily" and be fixable, not take the
 * screen down.
 */
export function cadenceLabel(action: CadenceFields): string {
  const at = action.cadence_at ?? null
  switch (action.cadence) {
    case 'instant':
      return 'Instantly'
    case 'hourly':
      return 'Hourly'
    case 'daily':
      return at ? `Daily at ${at}` : 'Daily'
    case 'weekly': {
      const day = WEEKDAYS[action.cadence_day ?? -1]
      if (!day) return at ? `Weekly at ${at}` : 'Weekly'
      return at ? `${day}s at ${at}` : `${day}s`
    }
  }
}

/**
 * The cadence select's options, in the fixed order RULE-033 names them in.
 *
 * The CADENCE ALONE — "Daily", not "Daily at 08:00". The time and the day are
 * chosen by the controls beside the select, and an option that already named
 * one would say the choice had been made there. The chip on the row is where
 * the three come back together, which is what `cadenceLabel` is for.
 */
export const CADENCE_OPTIONS = CADENCES.map((cadence) => ({
  value: cadence,
  label: cadenceLabel({ cadence }),
}))

/**
 * Change an action's cadence, and bring its two companion fields with it.
 *
 * THIS IS NOT COSMETIC. `crossReference` in `funnel.ts` refuses a funnel where
 * they disagree, in both directions and for a stated reason:
 *
 *   · "a daily action needs a time — the digest scheduler selects on it"
 *   · "an instant action fires on arrival, so a time means nothing here"
 *   · "a weekly action needs a day of the week (0 = Sunday)"
 *
 * and `services/funnels/draft.ts` parses the whole funnel on every autosave. So
 * a UI that set `cadence` and left a stale `cadence_at` behind would not render
 * wrong — it would make the draft unsaveable, three keystrokes after the last
 * successful save, with the failure surfacing as "That change isn't saved yet."
 *
 * It lives here rather than in the component for exactly that reason: a rule
 * whose violation is a 400 belongs somewhere a test can reach it.
 */
export function withCadence(action: RoutingAction, cadence: Cadence): RoutingAction {
  const timed = cadence === 'daily' || cadence === 'weekly'
  const next: RoutingAction = { ...action, cadence }

  if (timed) next.cadence_at = action.cadence_at ?? DEFAULT_CADENCE_AT
  else delete next.cadence_at

  if (cadence === 'weekly') next.cadence_day = action.cadence_day ?? DEFAULT_CADENCE_DAY
  else delete next.cadence_day

  return next
}

/* ── an action, before it has been configured ───────────────────────────── */

/**
 * A new action of a kind, valid the moment it exists.
 *
 * `instant` and NO RECIPIENTS. An action that fired the moment it was added, to
 * an address nobody had chosen yet, is the failure RULE-067 exists to prevent —
 * `services/notify/recipients.ts` would refuse the send, but the right shape is
 * one where there is nothing to refuse.
 */
export function newRoutingAction(id: string, kind: ActionKind): RoutingAction {
  return {
    id,
    kind,
    recipients: [],
    cadence: 'instant',
    config: {},
    enabled: true,
  }
}

/* ── which actions fire, and when ─────────────────────────────── */

/**
 * The enabled actions a band's rule holds. Pure, and reading the SNAPSHOT.
 *
 * RULE-059 is why this takes a funnel rather than a database: "**Never gate a
 * submission on a D1 read**". The submission path already holds the published
 * schema on the KV pointer, so asking it which actions a band fires costs
 * nothing, while `notification_actions` — the same information as rows — would
 * be a read on the one path that may not have one. The projection exists for
 * the opposite question, which no request can answer: "which actions fire at
 * 08:00 on Monday, across every funnel" (DECISION-007).
 *
 * A DISABLED ACTION IS NOT ONE. `services/funnels/publish.ts` filters the same
 * way and says why: "it cannot fire, and a scheduler that reads it and skips it
 * pays for it on every tick forever."
 *
 * Story 9.2.
 */
export function actionsForBand(
  funnel: { routing: { band_id: string; actions: RoutingAction[] }[] },
  bandId: string,
): RoutingAction[] {
  const rule = funnel.routing.find((entry) => entry.band_id === bandId)
  return (rule?.actions ?? []).filter((action) => action.enabled)
}

/**
 * The ones that fire the moment a lead is scored.
 *
 * Everything else is a digest and belongs to the scheduler (Story 9.4), which
 * asks `notification_actions(cadence, cadence_at)` the same question on a clock
 * instead of on a submission. Splitting them HERE rather than in the dispatcher
 * is what keeps that division a property of the vocabulary: there is one place
 * that decides what "now" means, and both readers call it.
 */
export const firesNow = (action: RoutingAction): boolean => action.cadence === 'instant'

/**
 * What an action row says it does, in the operator's own terms.
 *
 * "Email alice@ and the sales inbox" in the Visual Reference — a sentence about
 * the recipients, not about the kind. An action with none yet says so, because
 * the row is the only place that omission is visible.
 */
export function describeAction(action: RoutingAction): string {
  const entry = ACTION_CATALOGUE[action.kind]
  const recipients = action.recipients.filter((address) => address.trim().length > 0)
  if (recipients.length === 0) return `${entry.label} — no recipients yet`
  if (recipients.length <= 2) return `${entry.label} ${recipients.join(' and ')}`
  return `${entry.label} ${recipients[0]} and ${recipients.length - 1} others`
}

/* ── when a digest fires ─────────────────────────────────────────────── */

/**
 * RULE-034, and it governs digests as well as business hours: "business hours
 * are configured **per funnel** as a timezone plus weekly recurring ranges…
 * **evaluate in the funnel's timezone, never the server's**".
 *
 * A Worker's clock is UTC everywhere, so "never the server's" is not a warning
 * about a misconfigured machine — it is a warning about code that never asks. A
 * daily digest at 08:00 is a promise about the operator's morning, and a
 * scheduler that compares `08:00` against `new Date().getUTCHours()` keeps that
 * promise for exactly one timezone and breaks it twice a year for the rest.
 */
export const DEFAULT_TIMEZONE = 'UTC'

/**
 * Every UTC offset an IANA zone currently uses, in minutes.
 *
 * NOT A DECORATION — it is what keeps the scheduler's query indexed. At any one
 * instant the world holds at most this many distinct local times, so "which
 * daily actions fire now" can ask `cadence_at IN (…)` over a list this long and
 * SEEK into `idx_notifactions_cadence`, instead of reading every daily action
 * and comparing in JavaScript (RULE-056: "an unindexed filter… is a cost bug").
 *
 * Offsets that no longer exist are harmless — they widen the candidate list by
 * one and match nothing. An offset MISSING from it is not: a funnel in that zone
 * would never be selected, and its digest would silently never send. So the list
 * errs long, and `test/digest.test.ts` checks it against `Intl`'s own answer for
 * a spread of real zones rather than against itself.
 */
export const UTC_OFFSETS_MINUTES: readonly number[] = [
  -720, -660, -600, -570, -540, -480, -420, -360, -300, -240, -210, -180,
  /**
   * −02:30 and +13:45 — Newfoundland and Chatham on DAYLIGHT time.
   *
   * Both were found by the coverage test rather than by reasoning, and the way
   * they were missing is the point: a list written from memory holds the odd
   * standard offsets and omits their summer variants, so every funnel in those
   * zones would have gone unselected for half the year with no error anywhere.
   * The test compares this list against `Intl`'s own answer for every zone it
   * can name, at two points in the year — never against itself.
   */
  -150,
  -120, -60, 0, 60, 120, 180, 210, 240, 270, 300, 330, 345, 360, 390, 420, 480,
  525, 540, 570, 600, 630, 660, 690, 720, 765, 780, 825, 840,
]

/** A moment, as one funnel's own clock reads it. */
export type LocalMoment = {
  /** `HH:MM`, 24-hour. What `cadence_at` is compared against. */
  time: string
  /** 0 = Sunday, matching `WEEKDAYS` and `cadence_day`. */
  weekday: number
  /** `YYYY-MM-DD` in that zone — the day a daily period is named by. */
  date: string
}

const pad = (value: number): string => value.toString().padStart(2, '0')

/**
 * What time it is, where the funnel is.
 *
 * `Intl` rather than arithmetic, because DST is not arithmetic: London is +0 in
 * January and +1 in July, Lord Howe shifts by thirty minutes, and a table of
 * offsets maintained by hand would be wrong the first time a government moved a
 * date. The runtime already carries the zone database.
 *
 * A zone it does not recognise falls back to UTC rather than throwing. A
 * scheduler that dies on one bad row stops sending every OTHER funnel's digest
 * too, which is RULE-078's failure needing a human — for a typo in a string.
 */
export function localMoment(timezone: string, at: number): LocalMoment {
  const parts = formatterFor(timezone).formatToParts(new Date(at))
  const value = (type: string): string => parts.find((part) => part.type === type)?.value ?? ''

  const weekday = WEEKDAYS.findIndex((day) => day.startsWith(value('weekday')))
  return {
    time: `${value('hour')}:${value('minute')}`,
    weekday: weekday < 0 ? 0 : weekday,
    date: `${value('year')}-${value('month')}-${value('day')}`,
  }
}

/**
 * `Intl.DateTimeFormat` is expensive to construct and free to reuse, and the
 * scheduler builds one per funnel per tick. Cached by zone, which is safe
 * because a formatter holds no time — only the rules for reading one.
 */
const formatters = new Map<string, Intl.DateTimeFormat>()

function formatterFor(timezone: string): Intl.DateTimeFormat {
  const cached = formatters.get(timezone)
  if (cached) return cached

  let formatter: Intl.DateTimeFormat
  try {
    formatter = build(timezone)
  } catch {
    formatter = build(DEFAULT_TIMEZONE)
  }
  formatters.set(timezone, formatter)
  return formatter
}

const build = (timeZone: string): Intl.DateTimeFormat =>
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    hourCycle: 'h23',
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

/**
 * Every `HH:MM` that is the local time SOMEWHERE at this instant.
 *
 * The scheduler's `IN` list. Deduplicated and sorted so the query text is
 * stable for a given minute, which makes it readable in a log and cacheable by
 * D1's statement cache.
 */
export function candidateLocalTimes(at: number): string[] {
  const utcMinutes = Math.floor(at / 60_000)
  const times = new Set<string>()
  for (const offset of UTC_OFFSETS_MINUTES) {
    const local = ((utcMinutes + offset) % 1440 + 1440) % 1440
    times.add(`${pad(Math.floor(local / 60))}:${pad(local % 60)}`)
  }
  return [...times].sort()
}

/**
 * Does this action fire at this local moment?
 *
 * The database narrowed the candidates by `(cadence, cadence_at)`; this is the
 * exact answer for the one funnel's own clock, and it is deliberately separate
 * so the arithmetic can be tested without a database at all.
 */
export function digestFires(
  action: { cadence: Cadence; cadence_at?: string | null; cadence_day?: number | null },
  local: LocalMoment,
): boolean {
  switch (action.cadence) {
    // Never here: an instant action fires on the submission, not on a clock.
    case 'instant':
      return false
    // On the hour, wherever the funnel is. A zone at :30 fires at :30 UTC.
    case 'hourly':
      return local.time.endsWith(':00')
    case 'daily':
      return action.cadence_at === local.time
    case 'weekly':
      return action.cadence_at === local.time && action.cadence_day === local.weekday
  }
}

/**
 * The period this firing belongs to, as a stable string.
 *
 * THE IDEMPOTENCY KEY. A tick that runs twice — a retried cron, two invocations
 * racing — must not send two digests, and the delivery id is derived from this
 * plus the action id. Two runs in the same period produce the same id, the
 * consumer finds a row that already exists, and nothing sends twice.
 *
 * Named in the FUNNEL's local terms, so a daily digest is one per local day
 * rather than one per UTC day — which is the same rule as the send time and
 * would be a second bug if it were not.
 */
export function periodKey(cadence: Cadence, local: LocalMoment): string {
  if (cadence === 'hourly') return `${local.date}T${local.time.slice(0, 2)}`
  if (cadence === 'weekly') return `${local.date}W`
  return local.date
}

/**
 * How far back a period reaches, in milliseconds.
 *
 * The window a digest covers is the one since the last time this action fired,
 * which for a fixed cadence is its own length. Approximate at a DST boundary by
 * up to an hour, and deliberately so: the alternative is remembering when each
 * action last sent, and a digest that covers 23 or 25 hours once a year is a
 * better trade than a column that can go stale.
 */
export const PERIOD_MS: Record<Exclude<Cadence, 'instant'>, number> = {
  hourly: 60 * 60 * 1000,
  daily: 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
}

/* ── the default configuration ───────────────────────────────────────── */

/**
 * PRD §3.10: "Default configuration: instant for hot, daily digest for the
 * rest."
 *
 * Applied to whatever bands the new funnel has, so a funnel created from a
 * blueprint with a three-band ladder arrives configured exactly as that
 * sentence describes, and a blank one — which `blankFunnel` gives a single
 * unbanded placeholder — arrives with one daily digest waiting for an address.
 *
 * NO RECIPIENTS, and that is the line between a default and an invention. The
 * PRD's default is about CADENCE; who should be told is a question only the
 * operator can answer, and guessing at the creating user would put an address
 * into a funnel's routing that nobody chose. The Connect tab renders the row as
 * "Email — no recipients yet", which is where that gap belongs — and
 * `messagesFor` will not enqueue an action addressed to nobody, so a seeded
 * default that is never finished costs nothing and sends nothing.
 */
export function defaultRouting(
  bands: readonly { id: string; tone: string }[],
  // `position`, not `index`. RULE-001 reserves that word for the thing D1 and
  // Vectorize already own, and `tests/rule-001-score-not-index.test.ts` refuses
  // it as an identifier anywhere in this package — including as a callback
  // parameter, which is where it slipped in.
  id: (position: number) => string,
): { band_id: string; actions: RoutingAction[] }[] {
  return bands.map((band, position) => {
    const action = newRoutingAction(id(position), 'email')
    return {
      band_id: band.id,
      actions: [
        band.tone === 'hot'
          ? action
          : { ...action, cadence: 'daily' as const, cadence_at: DEFAULT_CADENCE_AT },
      ],
    }
  })
}
